import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { undoToast } from "@/lib/undo";
import { useInvalidate } from "@/lib/queries";

const KIND = { entity: "Entry", event: "Event", timeline: "Timeline", pin: "Map pin", region: "Map region", map: "Map", jump: "Time jump" };
const keyOf = (o) => `${o.kind}:${o.id}:${o.field}`;

export const RenameDialog = ({ open, onOpenChange, ent, oldName, newName: initialNew = "" }) => {
  const invalidate = useInvalidate();
  const [newName, setNewName] = useState(initialNew);
  const [preview, setPreview] = useState(null);
  const [picked, setPicked] = useState({});
  const [keepAlias, setKeepAlias] = useState(true);
  const [busy, setBusy] = useState(false);
  const from = oldName || ent.name;

  const load = async (name) => {
    const { data } = await api.post(`/entities/${ent.id}/rename/preview`, { old_name: from, new_name: name || from });
    setPreview(data);
    setPicked(Object.fromEntries(data.occurrences.map((o) => [keyOf(o), true])));
  };
  useEffect(() => { load(initialNew).catch((e) => toast.error(errMsg(e))); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = async () => {
    setBusy(true);
    try {
      const targets = preview.occurrences.filter((o) => picked[keyOf(o)]).map(({ kind, id, field }) => ({ kind, id, field }));
      const { data } = await api.post(`/entities/${ent.id}/rename/apply`, { old_name: from, new_name: newName, targets, keep_alias: keepAlias });
      invalidate(["entities", ent.universe_id], "entity", "events", ["timelines", ent.universe_id], "pins", "regions", "maps", ["jumps", ent.universe_id]);
      undoToast({ ...ent, name: from }, `Renamed to ${data.name}`, invalidate, { description: `${data.replaced} spellings corrected · Undo restores this entry's name and text` });
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const same = !newName.trim() || newName.trim() === from;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto lf-scroll" data-testid="rename-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl">Rename everywhere</DialogTitle>
          <DialogDescription>Change the spelling of “{from}” and correct it across your universe.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label>New name</Label>
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={from} data-testid="rename-new-name" />
          {preview?.clash && newName.trim().toLowerCase() !== from.toLowerCase() && <p className="text-xs text-red-400" data-testid="rename-clash">“{preview.clash}” already uses a similar name.</p>}
        </div>
        <OccurrenceList preview={preview} picked={picked} setPicked={setPicked} from={from} />
        <label className="flex items-center gap-2 text-sm text-[#A8A090]">
          <Checkbox checked={keepAlias} onCheckedChange={(v) => setKeepAlias(!!v)} data-testid="rename-keep-alias" />Keep “{from}” as an alias so old notes still match
        </label>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="rename-cancel-button">{initialNew ? "Skip" : "Cancel"}</Button>
          <Button onClick={apply} disabled={busy || same || !preview} data-testid="rename-apply-button">{busy ? "Renaming…" : "Rename & correct"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

function OccurrenceList({ preview, picked, setPicked, from }) {
  if (!preview) return <p className="text-sm text-[#A8A090]">Searching for “{from}”…</p>;
  if (!preview.occurrences.length) return <p className="text-sm text-[#A8A090]" data-testid="rename-no-occurrences">“{from}” isn't written anywhere else. Only the entry name changes.</p>;
  const all = preview.occurrences.every((o) => picked[keyOf(o)]);
  return (
    <div className="space-y-2" data-testid="rename-occurrences">
      <div className="flex items-center justify-between">
        <div className="lf-label">{preview.total} mentions in {preview.occurrences.length} places</div>
        <button type="button" className="text-xs text-[#A8A090] hover:text-[#F59E0B]" data-testid="rename-toggle-all"
          onClick={() => setPicked(Object.fromEntries(preview.occurrences.map((o) => [keyOf(o), !all])))}>{all ? "Select none" : "Select all"}</button>
      </div>
      <div className="max-h-[40vh] overflow-y-auto lf-scroll space-y-1.5 rounded-md border border-[#2D3442] bg-[#0F131A] p-2">
        {preview.occurrences.map((o) => (
          <label key={keyOf(o)} className="flex cursor-pointer items-start gap-2.5 rounded px-2 py-2 hover:bg-[#1A202C]">
            <Checkbox className="mt-0.5" checked={!!picked[keyOf(o)]} onCheckedChange={(v) => setPicked({ ...picked, [keyOf(o)]: !!v })} data-testid={`rename-occ-${o.kind}-${o.id}-${o.field}`} />
            <div className="min-w-0 text-sm">
              <div className="text-[#F3EBDD]">{o.label}{o.is_self && " (this entry)"} <span className="text-xs text-[#A8A090]">· {KIND[o.kind]} {o.field} · {o.count}×</span></div>
              <div className="truncate text-xs text-[#A8A090]">{o.snippet}</div>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}
